// acta_patch_lot3_quotes_foundation_v1
// ACTA_DAY_NIGHT_THEME_SWITCH_V52
// ACTA_DESKTOP_WORKSPACE_SHELL_V2
// ACTA_SETTINGS_CONTEXTUAL_NAV_V3
// acta_patch_machine_catalog_settings_tabs_remove_price_v1
// acta_patch_lot2_clients_machine_catalog_v1
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTenantBranding } from "@/context/TenantBrandingContext";
import { useOptionalTenantFeatures } from "@/context/TenantFeaturesContext";
import {
  hasModuleAccess,
  hasPageAccess,
} from "@/lib/accessPermissions";
import {
  normalizeActaTheme,
  useTheme,
} from "@/context/ThemeContext";
import NotificationsDropdown from "@/components/NotificationsDropdown";
import ActaAgent from "@/agent/ActaAgent";
import ShareModal from "@/components/ShareModal";
import { updateMe } from "@/api/auth";
import { mail as mailApi } from "@/api";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  House, Folder, Users as UsersIcon, ChartLineUp, UserList,
  Gear, SignOut, EnvelopeOpen, List, X, ShieldCheck,
  MapPin, CalendarCheck, Wrench, Package,
  CaretDown, Briefcase, FileText, ChatCircleDots,
  UserCircle, Planet, Sun, Moon,
  PaperPlaneTilt, Star, Archive, Trash, Warning,
  PencilSimple, FolderPlus, AddressBook, Clock, Megaphone,
} from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const NAV = [
  {
    to: "/app/acquisition/dashboard",
    label: "Acquisition",
    icon: Megaphone,
    feature: "acquisitionEnabled",
    roles: ["admin"],
  },
  {
    to: "/app/documents",
    label: "Documents",
    icon: Folder,
    feature: "documentsEnabled",
  },
  {
    to: "/app/mail",
    label: "Messagerie",
    icon: EnvelopeOpen,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
      "technician",
    ],
  },
  {
    to: "/monpremiermonitoring.fr/dashboard",
    label: "Supervision",
    icon: ShieldCheck,
    roles: ["saas_admin"],
  },
  {
    to: "/app/settings",
    label: "Paramètres",
    icon: Gear,
  },
];


const ACQUISITION_NAV = [
  {
    to: "/app/acquisition/dashboard",
    page: "dashboard",
    label: "Tableau de bord",
    icon: House,
    end: true,
  },
  {
    to: "/app/acquisition/leads",
    page: "leads",
    label: "Leads",
    icon: AddressBook,
  },
  {
    to: "/app/acquisition/campaigns",
    page: "campaigns",
    label: "Campagnes",
    icon: Megaphone,
  },
  {
    to: "/app/acquisition/connections",
    page: "connections",
    label: "Connexions",
    icon: ChatCircleDots,
  },
  {
    to: "/app/acquisition/settings",
    page: "settings",
    label: "Paramètres",
    icon: Gear,
  },
];

const MAIL_NAV = [
  {
    to: "/app/mail?compose=1",
    page: "mail",
    label: "Nouveau mail",
    icon: PencilSimple,
    action: true,
    mailFolder: "compose",
  },
  {
    to: "/app/mail?addressBook=1",
    page: "mail",
    label: "Carnet d’adresses",
    icon: AddressBook,
    mailFolder: "address-book",
  },
  {
    to: "/app/mail?folder=inbox",
    page: "mail",
    label: "Boîte de réception",
    icon: EnvelopeOpen,
    mailFolder: "inbox",
  },
  {
    to: "/app/mail?folder=sent",
    page: "mail",
    label: "Envoyés",
    icon: PaperPlaneTilt,
    mailFolder: "sent",
  },
  {
    to: "/app/mail?folder=scheduled",
    page: "mail",
    label: "Planifiés",
    icon: Clock,
    mailFolder: "scheduled",
  },
  {
    to: "/app/mail?folder=starred",
    page: "mail",
    label: "Favoris",
    icon: Star,
    mailFolder: "starred",
  },
  {
    to: "/app/mail?folder=archive",
    page: "mail",
    label: "Archives",
    icon: Archive,
    mailFolder: "archive",
  },
  {
    to: "/app/mail?folder=spam",
    page: "mail",
    label: "Spam",
    icon: Warning,
    mailFolder: "spam",
  },
  {
    to: "/app/mail?folder=trash",
    page: "mail",
    label: "Corbeille",
    icon: Trash,
    mailFolder: "trash",
  },
];

const MAIL_MOBILE_QUICK_FOLDERS = new Set([
  "compose",
  "inbox",
  "sent",
  "trash",
]);


const COMMERCE_NAV = [
  {
    to: "/app/commerce/dashboard",
    page: "dashboard",
    label: "Vue d’ensemble",
    icon: House,
    end: true,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ],
  },
  {
    to: "/app/commerce/pipeline",
    page: "pipeline",
    label: "Affaires",
    icon: Briefcase,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ],
  },
  {
    to: "/app/commerce/agenda",
    page: "agenda",
    label: "Agenda",
    icon: CalendarCheck,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ],
  },
  {
    to: "/app/commerce/workshops",
    page: "workshops",
    label: "Workshops",
    icon: CalendarCheck,
    roles: ["admin", "manager", "user"],
  },
  {
    to: "/app/commerce/prospects",
    page: "portfolio",
    label: "Contacts",
    icon: UserList,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ],
  },
  {
    to: "/app/commerce/stats",
    page: "stats",
    label: "Statistiques",
    icon: ChartLineUp,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ],
  },
  {
    to: "/app/commerce/map",
    page: "map",
    label: "Carte",
    icon: MapPin,
    roles: [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ],
  },

];


const COMMERCE_ACTIVE_PATHS = [
  "/app/commerce",
  "/app/prospects",
  "/app/stats",
  "/app/map",
];


function isCommercePath(pathname) {
  return COMMERCE_ACTIVE_PATHS.some(
    (path) =>
      pathname === path ||
      pathname.startsWith(
        `${path}/`
      )
  );
}


const GMAO_NAV = [
  {
    to: "/app/gmao/v2/dashboard",
    label: "Dashboard",
    page: "dashboard",
    technicianLabel: "Dashboard",
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },

  {
    to: "/app/gmao/v2/parc",
    label: "Parc",
    page: "machines",
    technicianLabel: "Parc machines",
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },

  {
    to: "/app/gmao/v2/planning",
    label: "Planning",
    page: "planning",
    technicianLabel: "Planning",
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },

  {
    to: "/app/gmao/v2/installations",
    label: "Installations",
    page: "installations",
    technicianLabel: "Installations",
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },

  {
    to: "/app/gmao/sav",
    label: "SAV",
    page: "sav",
    technicianLabel: "SAV",
    icon: ChatCircleDots,
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },

  {
    to: "/app/gmao/maintenance",
    label: "Maintenance",
    page: "maintenance",
    technicianLabel: "Maintenance",
    icon: Wrench,
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },

  {
    to: "/app/gmao/map",
    label: "Carte",
    page: "machines",
    technicianLabel: "Carte",
    icon: MapPin,
    roles: [
      "admin",
      "maintenance_manager",
      "technician",
    ],
    feature: "gmaoEnabled",
  },
];

function roleLabel(role) {
  return ({
    admin: "Administrateur",
    manager: "Manager",
    maintenance_manager: "Responsable de maintenance",
    user: "Commercial",
    technician: "Technicien",
    super_admin: "Super Admin",
    saas_admin: "SaaS Admin",
  })[role] || role;
}

const DOCUMENTS_NAV = [];

const SETTINGS_NAV = [
  {
    to: "/app/settings?section=profile",
    settingsSection: "profile",
    label: "Profil & compte",
    icon: UserCircle,
  },
  {
    to: "/app/settings?section=mail_account",
    settingsSection: "mail_account",
    label: "Ma messagerie",
    icon: EnvelopeOpen,
    accessModule: "mail",
    accessPage: "mail",
  },
  {
    to: "/app/settings?section=secure_share",
    settingsSection: "secure_share",
    label: "Partage sécurisé",
    icon: EnvelopeOpen,
  },
  {
    to: "/app/settings?section=security",
    settingsSection: "security",
    label: "Sécurité",
    icon: ShieldCheck,
  },
  {
    to: "/app/settings?section=notifications",
    settingsSection: "notifications",
    label: "Notifications",
    icon: EnvelopeOpen,
  },
  {
    to: "/app/settings?section=users",
    settingsSection: "users",
    label: "Utilisateurs",
    maintenanceLabel: "Techniciens",
    icon: UsersIcon,
    roles: [
      "admin",
      "manager",
      "maintenance_manager",
    ],
  },
  {
    to: "/app/settings?section=machine_catalog",
    settingsSection: "machine_catalog",
    label: "Catalogue machines",
    icon: Wrench,
    roles: ["admin"],
    feature: "gmaoEnabled",
    accessModule: "gmao",
    accessPage: "machines",
  },
];

const SUPERVISION_NAV = [
  {
    to: "/monpremiermonitoring.fr/dashboard?section=global",
    supervisionSection: "global",
    label: "Vue d’ensemble",
    icon: House,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=server",
    supervisionSection: "server",
    label: "Serveur",
    icon: ChartLineUp,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=security",
    supervisionSection: "security",
    label: "Sécurité",
    icon: ShieldCheck,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=rgpd",
    supervisionSection: "rgpd",
    label: "RGPD",
    icon: ShieldCheck,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=accounts",
    supervisionSection: "accounts",
    label: "Comptes",
    icon: UsersIcon,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=support",
    supervisionSection: "support",
    label: "Support",
    icon: ChatCircleDots,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=logs",
    supervisionSection: "logs",
    label: "Logs / Audit",
    icon: FileText,
  },
  {
    to: "/monpremiermonitoring.fr/dashboard?section=profile",
    supervisionSection: "profile",
    label: "Mon compte",
    icon: UserCircle,
  },
];

const MOBILE_ROUTE_TITLES = [
  ["/app/acquisition/dashboard", "Acquisition"],
  ["/app/acquisition/leads", "Leads"],
  ["/app/acquisition/campaigns", "Campagnes"],
  ["/app/acquisition/connections", "Connexions"],
  ["/app/acquisition/settings", "Paramètres Acquisition"],
  ["/app/commerce/dashboard", "Accueil commercial"],
  ["/app/commerce/pipeline", "Affaires"],
  ["/app/commerce/quotes", "Affaires"],
  ["/app/commerce/agenda", "Agenda"],
  ["/app/commerce/prospects", "Portefeuille"],
  ["/app/commerce/stats", "Statistiques"],
  ["/app/commerce/map", "Carte"],
  ["/app/gmao/dashboard", "Ma journée"],
  ["/app/gmao/installations", "Installations"],
  ["/app/gmao/map", "Carte"],
  ["/app/gmao/sav", "Tickets SAV"],
  ["/app/gmao/interventions", "Interventions"],
  ["/app/gmao/planning", "Planning"],
  ["/app/gmao/maintenance", "Maintenance"],
  ["/app/gmao/machines", "Machines"],
  ["/app/gmao/parts", "Stock & pièces"],
  ["/app/documents", "Documents"],
  ["/app/mail", "Messagerie"],
  ["/app/settings", "Paramètres"],
  ["/app/support", "Mes signalements"],
  ["/app/gmao/v2/dashboard", "Dashboard GMAO"],
  ["/app/gmao/v2/parc", "Parc"],
  ["/app/gmao/v2/planning", "Planning"],
  ["/app/gmao/v2/installations", "Installations"],


];

function mobilePageTitle(pathname) {
  const match = MOBILE_ROUTE_TITLES.find(
    ([path]) => pathname === path || pathname.startsWith(`${path}/`)
  );

  return match?.[1] || "Acta Documentum";
}

const MOBILE_LAUNCHER_THEMES = {
  commercial: {
    accent: "var(--do-accent)",
    accentStrong: "var(--do-accent-strong)",
    accentSoft: "var(--do-accent-soft)",
  },
  gmao: {
    accent: "var(--do-accent)",
    accentStrong: "var(--do-accent-strong)",
    accentSoft: "var(--do-accent-soft)",
  },
  documents: {
    accent: "var(--do-accent)",
    accentStrong: "var(--do-accent-strong)",
    accentSoft: "var(--do-accent-soft)",
  },
  settings: {
    accent: "var(--do-accent)",
    accentStrong: "var(--do-accent-strong)",
    accentSoft: "var(--do-accent-soft)",
  },
  root: {
    accent: "var(--do-accent)",
    accentStrong: "var(--do-accent-strong)",
    accentSoft: "var(--do-accent-soft)",
  },
};

function mobileLauncherPosition(_spaceKey, _item, index, count) {
  if (!Number.isFinite(count) || count <= 0) {
    return { x: 50, y: 50 };
  }

  // Une seule règle pour tous les modules :
  // les pages sont réparties régulièrement sur la même orbite.
  const radius =
    count <= 3
      ? 34
      : count <= 5
        ? 38
        : count <= 7
          ? 39
          : 40;

  const angle =
    (-Math.PI / 2)
    + ((Math.PI * 2 * index) / count);

  return {
    x: 50 + (Math.cos(angle) * radius),
    y: 50 + (Math.sin(angle) * radius),
  };
}



export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const branding = useTenantBranding();
  const tenantFeatures =
    useOptionalTenantFeatures();

  const hasFeature =
    tenantFeatures?.hasFeature ||
    (() => false);

  const {
    theme,
    themeOwnerId,
    setThemeForUser,
    hydrateThemeForUser,
    resetThemeSession,
  } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
  const [mobileLauncherSpace, setMobileLauncherSpace] = useState(null);
  const [mobileLauncherQuick, setMobileLauncherQuick] = useState(false);

  const [
    mailFolders,
    setMailFolders,
  ] = useState([]);

  // ACTA_AGENT_UNIVERSAL_STATE_V1
  const [
    actaAgentOpen,
    setActaAgentOpen,
  ] = useState(false);

  const [
    agentPreparedShare,
    setAgentPreparedShare,
  ] = useState(null);

  const [
    themeSaving,
    setThemeSaving,
  ] = useState(false);
  const [
    commerceOpen,
    setCommerceOpen,
  ] = useState(
    isCommercePath(
      location.pathname
    )
  );
  const [gmaoOpen, setGmaoOpen] = useState(
    location.pathname.startsWith("/app/gmao")
  );

  /**
   * La Supervision reste un espace plateforme distinct
   * pour la navigation et les permissions.
   */
  const isSupervisionSpace =
    location.pathname.startsWith(
      "/monpremiermonitoring.fr"
    );

  /**
   * Identifiant du compte connecté.
   */
  const currentUserId = String(user?.id || "");

  /**
   * Utilise le thème du contexte uniquement lorsqu'il appartient
   * bien à l'utilisateur actuellement connecté.
   */
  const contextBelongsToCurrentUser =
    currentUserId &&
    themeOwnerId === currentUserId;

  /**
   * La préférence renvoyée par /auth/me reste la source de vérité.
   */
  const accountTheme = normalizeActaTheme(
    user?.themePreference || "light"
  );

  const tenantTheme = contextBelongsToCurrentUser
    ? theme
    : accountTheme;

  /**
   * Apparence fixe de la Supervision.
   *
   * Le thème Dark sert de fondation technique,
   * puis des variables spécifiques Supervision l'écrasent en CSS.
   */
  /**
   * Tous les espaces privés Acta utilisent la même
   * préférence Jour / Nuit du compte connecté.
   */
  const appliedTheme =
    normalizeActaTheme(tenantTheme);

  const appSpace = isSupervisionSpace
    ? "supervision"
    : "tenant";

  // Ferme le tiroir mobile lors d’un changement de page.
  // Le menu GMAO reste ouvert sur toutes ses sous-routes.
  useEffect(() => {
    setDrawerOpen(false);
    setMobileMoreOpen(false);
    setMobileAccountOpen(false);
    setMobileLauncherSpace(null);
    setActaAgentOpen(false);
    setAgentPreparedShare(null);

    if (
      isCommercePath(
        location.pathname
      )
    ) {
      setCommerceOpen(true);
    }

    if (
      location.pathname.startsWith(
        "/app/gmao"
      )
    ) {
      setGmaoOpen(true);
    }
  }, [location.pathname]);

  /**
   * Charge le thème personnel depuis le compte connecté.
   *
   * La même préférence est appliquée à Paramètres
   * et à Supervision.
   */
  useEffect(() => {
    if (!user?.id) return;

    hydrateThemeForUser(user);
  }, [
    user?.id,
    user?.themePreference,
    hydrateThemeForUser,
  ]);

  // Prevent body scroll while a mobile navigation layer is open.
  useEffect(() => {
    const locked = drawerOpen || mobileMoreOpen || mobileAccountOpen;
    document.body.style.overflow = locked ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen, mobileMoreOpen, mobileAccountOpen]);

  useEffect(() => {
    if (!mobileMoreOpen && !mobileAccountOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      setMobileMoreOpen(false);
      setMobileAccountOpen(false);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMoreOpen, mobileAccountOpen]);

  /**
   * Applique le thème uniquement au shell authentifié.
   *
   * Landing, Login, pages légales et PublicShare
   * restent complètement indépendants.
   */
  useEffect(() => {
    const body = document.body;

    const previousTheme =
      body.dataset.actaTheme;

    const previousSpace =
      body.dataset.actaSpace;

    const previousColorScheme =
      body.style.colorScheme;
    const browserThemeColor = document.querySelector(
      'meta[name="theme-color"]'
    );
    body.dataset.actaTheme = appliedTheme;
    body.dataset.actaSpace = appSpace;

    body.style.colorScheme =
      appliedTheme === "dark"
        ? "dark"
        : "light";

    // Keep the authenticated browser chrome in sync with V53.
    browserThemeColor?.setAttribute(
      "content",
      appliedTheme === "dark" ? "#101724" : "#E8DDD0"
    );

    delete document.documentElement.dataset
      .actaBootstrapTheme;
    document.documentElement.style
      .removeProperty("background-color");
    document.documentElement.style
      .removeProperty("color-scheme");

    return () => {
      if (previousTheme) {
        body.dataset.actaTheme = previousTheme;
      } else {
        delete body.dataset.actaTheme;
      }

      if (previousSpace) {
        body.dataset.actaSpace = previousSpace;
      } else {
        delete body.dataset.actaSpace;
      }

      body.style.colorScheme =
        previousColorScheme;

      // The HTML default belongs to public routes, not the previous account.
      browserThemeColor?.setAttribute("content", "#DCEEFF");
    };
  }, [appliedTheme, appSpace]);

  const handleThemeChange = async (nextTheme) => {
    const normalizedTheme = normalizeActaTheme(nextTheme);

    if (
      !currentUserId
      || normalizedTheme === appliedTheme
      || themeSaving
    ) {
      return;
    }

    const previousTheme = appliedTheme;

    setThemeForUser(currentUserId, normalizedTheme);
    setThemeSaving(true);

    try {
      await updateMe({
        theme_preference: normalizedTheme,
      });
    } catch (error) {
      setThemeForUser(currentUserId, previousTheme);
      toast.error(
        "Impossible d’enregistrer le thème. Le choix précédent a été restauré."
      );
    } finally {
      setThemeSaving(false);
    }
  };

  const onLogout = async () => {
    /**
     * Retire immédiatement le thème du compte courant
     * avant l'ouverture éventuelle d'un autre compte.
     */
    resetThemeSession();

    await logout();
    navigate("/login");
  };

  const featureVisibleNav = NAV.filter(
    (item) => {
      if (
        item.feature
        && !hasFeature(item.feature)
      ) {
        return false;
      }

      if (
        item.to === "/app/mail"
        && !hasPageAccess(
          user,
          "mail",
          "mail"
        )
      ) {
        return false;
      }

      if (
        item.to === "/app/documents"
        && !hasPageAccess(
          user,
          "documents",
          "documents"
        )
      ) {
        return false;
      }

      return true;
    }
  );

  const visibleNav =
    user.role === "saas_admin"
      ? NAV.filter(
          (item) =>
            item.to ===
              "/monpremiermonitoring.fr/dashboard" ||
            item.to === "/app/settings"
        )
      : user.role === "maintenance_manager"
        ? featureVisibleNav.filter(
            (item) =>
              item.to === "/app/documents" ||
              item.to === "/app/mail" ||
              item.to === "/app/settings" ||
              item.to === "/app/users"
          )
        : user.role === "technician"
          ? featureVisibleNav.filter(
              (item) =>
                item.to === "/app/mail"
                || item.to === "/app/settings"
            )
          : featureVisibleNav.filter(
              (item) =>
                item.to !==
                  "/monpremiermonitoring.fr/dashboard" &&
                (
                  !item.roles ||
                  item.roles.includes(user.role)
                )
            );

  const visibleCommerceNav =
    COMMERCE_NAV.filter(
      (item) =>
        item.roles.includes(user.role)
        && hasPageAccess(
          user,
          "commercial",
          item.page
        )
    );

  const canSeeCommerce =
    hasFeature("commercialEnabled")
    && [
      "admin",
      "manager",
      "user",
      "maintenance_manager",
    ].includes(user.role)
    && hasModuleAccess(
      user,
      "commercial"
    )
    && visibleCommerceNav.length > 0;

  const commerceActive =
    isCommercePath(
      location.pathname
    );

  const visibleGmaoNav =
    GMAO_NAV.filter(
      (item) =>
        (
          !item.roles
          || item.roles.includes(
            user.role
          )
        )
        && hasPageAccess(
          user,
          "gmao",
          item.page
        )
    );

  const canSeeGmao =
    hasFeature("gmaoEnabled")
    && hasModuleAccess(user, "gmao")
    && visibleGmaoNav.length > 0;

  const gmaoActive =
    location.pathname.startsWith(
      "/app/gmao"
    );

  const visibleAcquisitionNav =
    ACQUISITION_NAV.filter(
      (item) =>
        hasPageAccess(
          user,
          "acquisition",
          item.page
        )
    );

  const canSeeAcquisition =
    user.role === "admin"
    && hasFeature("acquisitionEnabled")
    && hasModuleAccess(user, "acquisition")
    && visibleAcquisitionNav.length > 0;

  const acquisitionActive =
    location.pathname.startsWith(
      "/app/acquisition"
    );

  const canSeeDocuments =
    hasFeature("documentsEnabled")
    && hasPageAccess(
      user,
      "documents",
      "documents"
    );

  const canSeeMail =
    hasPageAccess(
      user,
      "mail",
      "mail"
    );

  // ACTA_AGENT_UNIVERSAL_ACCESS_V1
  const canUseActaAgent =
    user.role !== "saas_admin"
    && (
      canSeeCommerce
      || canSeeGmao
      || canSeeDocuments
    );

  const canSeeSettings =
    visibleNav.some(
      (item) => item.to === "/app/settings"
    );

  const documentsActive =
    location.pathname.startsWith(
      "/app/documents"
    );

  const mailActive =
    location.pathname.startsWith(
      "/app/mail"
    );

  useEffect(
    () => {
      if (
        !canSeeMail
        || !mailActive
      ) {
        setMailFolders([]);
        return;
      }

      let active = true;

      const loadMailFolders =
        () => {
          mailApi
            .listFolders()
            .then((response) => {
              if (active) {
                setMailFolders(
                  response?.items || []
                );
              }
            })
            .catch(() => {
              if (active) {
                setMailFolders([]);
              }
            });
        };

      loadMailFolders();

      window.addEventListener(
        "acta:mail-folders-updated",
        loadMailFolders
      );

      return () => {
        active = false;
        window.removeEventListener(
          "acta:mail-folders-updated",
          loadMailFolders
        );
      };
    },
    [
      canSeeMail,
      mailActive,
      location.search,
    ]
  );

  const mailQuickNavigation = MAIL_NAV.filter(
    (item) => MAIL_MOBILE_QUICK_FOLDERS.has(item.mailFolder)
  );

  const mailNavigation = [
    ...MAIL_NAV,
    {
      to: "/app/mail?createFolder=1",
      page: "mail",
      label: "Nouveau dossier",
      icon: FolderPlus,
      mailFolder: "create-folder",
      action: true,
    },
    ...mailFolders.map(
      (customFolder) => ({
        to:
          "/app/mail?folder=custom&id="
          + encodeURIComponent(
            customFolder.id
          ),
        page: "mail",
        label: customFolder.name,
        icon: Folder,
        mailFolder:
          "custom:"
          + customFolder.id,
      })
    ),
  ];

  const mailCustomNavigation = mailNavigation.filter(
    (item) =>
      item.mailFolder === "create-folder"
      || item.mailFolder?.startsWith("custom:")
  );

  const settingsActive =
    location.pathname.startsWith(
      "/app/settings"
    )
    || location.pathname.startsWith(
      "/app/users"
    );

  const supervisionActive =
    location.pathname.startsWith(
      "/monpremiermonitoring.fr"
    );

  const visibleSettingsNav =
    SETTINGS_NAV.filter(
      (item) => {
        if (
          item.roles
          && !item.roles.includes(
            user.role
          )
        ) {
          return false;
        }

        if (
          item.feature
          && !hasFeature(item.feature)
        ) {
          return false;
        }

        if (
          item.accessModule
          && !hasModuleAccess(
            user,
            item.accessModule
          )
        ) {
          return false;
        }

        if (
          item.accessModule
          && item.accessPage
          && !hasPageAccess(
            user,
            item.accessModule,
            item.accessPage
          )
        ) {
          return false;
        }

        return true;
      }
    );

  const desktopGmaoNav = [
    ...visibleGmaoNav,
  ].sort((left, right) => {
    if (left.page === "dashboard") return -1;
    if (right.page === "dashboard") return 1;
    return 0;
  });

  const hybridCommercialMaintenance =
    user.role === "maintenance_manager"
    && canSeeCommerce
    && canSeeGmao;

  const desktopRoleLabel =
    user.role === "admin"
      ? "Administrateur · Tous les modules"
      : hybridCommercialMaintenance
        ? "Responsable maintenance + Commercial"
        : roleLabel(user.role);

  const mobileSpaces = [
    canSeeAcquisition && {
      key: "acquisition",
      label: "Acquisition",
      shortLabel: "Acquisition",
      icon: Megaphone,
      to: visibleAcquisitionNav.find(
        (item) => item.page === "dashboard"
      )?.to || visibleAcquisitionNav[0]?.to,
      active: acquisitionActive,
    },
    canSeeCommerce && {
      key: "commercial",
      label: "Commercial",
      shortLabel: "Commerce",
      icon: Briefcase,
      to: visibleCommerceNav.find(
        (item) => item.page === "dashboard"
      )?.to || visibleCommerceNav[0]?.to,
      active: commerceActive,
    },
    canSeeGmao && {
      key: "gmao",
      label: "GMAO",
      shortLabel: "GMAO",
      icon: Wrench,
      to: visibleGmaoNav.find(
        (item) => item.page === "dashboard"
      )?.to || visibleGmaoNav[0]?.to,
      active: gmaoActive,
    },
    canSeeDocuments && {
      key: "documents",
      label: "Documents",
      shortLabel: "Documents",
      icon: Folder,
      to: "/app/documents",
      active: documentsActive,
    },
    canSeeMail && {
      key: "mail",
      label: "Messagerie",
      shortLabel: "Messages",
      icon: EnvelopeOpen,
      to: "/app/mail",
      active: mailActive,
    },
    canSeeSettings && {
      key: "settings",
      label: "Paramètres",
      shortLabel: "Réglages",
      icon: Gear,
      to: "/app/settings",
      active: settingsActive,
    },
  ].filter(Boolean);

  const desktopWorkspaceSpaces = [
    user.role === "saas_admin" && {
      key: "supervision",
      label: "Supervision",
      shortLabel: "Supervision",
      icon: ShieldCheck,
      to: "/monpremiermonitoring.fr/dashboard",
      active: supervisionActive,
    },
    ...mobileSpaces,
  ].filter(Boolean);

  const activeSpaceKey = acquisitionActive
    ? "acquisition"
    : commerceActive
      ? "commercial"
      : gmaoActive
      ? "gmao"
      : documentsActive
        ? "documents"
        : mailActive
          ? "mail"
          : settingsActive
            ? "settings"
            : supervisionActive
              ? "supervision"
              : desktopWorkspaceSpaces[0]?.key
                || "settings";

  const activeDesktopNav = ({
    acquisition: visibleAcquisitionNav,
    commercial: visibleCommerceNav,
    gmao: desktopGmaoNav,
    documents: canSeeDocuments
      ? DOCUMENTS_NAV
      : [],
    mail: canSeeMail
      ? mailNavigation
      : [],
    settings: canSeeSettings
      ? visibleSettingsNav
      : [],
    supervision: user.role === "saas_admin"
      ? SUPERVISION_NAV
      : [],
  })[activeSpaceKey] || [];

  const desktopNavigationTitle = ({
    acquisition: "Espace acquisition",
    commercial: "Espace commercial",
    gmao: "Espace GMAO / SAV",
    documents: "Espace documents",
    mail: "Espace messagerie",
    settings: "Paramètres",
    supervision: "Supervision SaaS",
  })[activeSpaceKey] || "Navigation";

  const mobileNavigationGroups = mobileSpaces.map((space) => {
    if (space.key === "acquisition") {
      return {
        ...space,
        items: visibleAcquisitionNav,
      };
    }

    if (space.key === "commercial") {
      return {
        ...space,
        items: visibleCommerceNav,
      };
    }

    if (space.key === "gmao") {
      return {
        ...space,
        items: visibleGmaoNav,
      };
    }

    if (space.key === "documents") {
      return {
        ...space,
        items: [
          {
            to: "/app/documents",
            page: "documents",
            label: "Documents",
            icon: Folder,
            end: true,
          },
        ],
      };
    }

    if (space.key === "mail") {
      return {
        ...space,
        items: mailNavigation,
      };
    }

    return {
      ...space,
      items: visibleSettingsNav,
    };
  });

  const selectedMobileNavigationGroup =
    mobileLauncherSpace
      ? mobileNavigationGroups.find(
          (group) => group.key === mobileLauncherSpace
        ) || null
      : null;

  const mobileLauncherItems =
    mobileLauncherQuick
      && selectedMobileNavigationGroup?.key === "mail"
      ? mailQuickNavigation
      : selectedMobileNavigationGroup?.items || [];

  const mobileLauncherRootItems = mobileNavigationGroups.map((group) => ({
    ...group,
    label: group.shortLabel || group.label,
  }));

  const MobileLauncherHeadingIcon =
    selectedMobileNavigationGroup?.icon
    || Planet;

  const mobileLauncherVisualSpace =
    mobileLauncherSpace
    || activeSpaceKey
    || "root";
  const mobileLauncherTheme =
    MOBILE_LAUNCHER_THEMES[mobileLauncherVisualSpace]
    || MOBILE_LAUNCHER_THEMES.root;
  const mobileLauncherStyle = {
    "--launcher-accent": mobileLauncherTheme.accent,
    "--launcher-accent-strong": mobileLauncherTheme.accentStrong,
    "--launcher-accent-soft": mobileLauncherTheme.accentSoft,
  };

  const activeMobileNavigationGroup =
    mobileNavigationGroups.find(
      (group) => group.key === activeSpaceKey
    ) || mobileNavigationGroups[0] || null;

  const activeSpaceLabel = ({
    acquisition: "Acquisition",
    commercial: "Commercial",
    gmao: "GMAO",
    documents: "Documents",
    mail: "Messagerie",
    settings: "Paramètres",
    supervision: "Supervision",
  })[activeSpaceKey] || "Acta";

  const mobileNavigationLabel = (item) => {
    if (user.role === "technician" && item.technicianLabel) {
      return item.technicianLabel
        .replace("Mes ", "")
        .replace("Mon ", "");
    }

    if (item.page === "dashboard") return "Accueil";
    if (item.page === "portfolio") return "Contacts";
    if (item.page === "maintenance") return "Maintenance";
    if (item.page === "parts") return "Stock";
    if (item.page === "sav") return "SAV";
    if (item.page === "stats") return "Stats";

    return item.label;
  };

  const mobileNavigationItemIsActive = (item) =>
    item.end
      ? location.pathname === item.to
      : location.pathname === item.to
        || location.pathname.startsWith(`${item.to}/`);

  const currentMobileTitle = mobilePageTitle(
    location.pathname
  );

  const navigateMobile = (to) => {
    if (!to) return;
    setMobileMoreOpen(false);
    setMobileAccountOpen(false);
    setMobileLauncherSpace(null);
    setMobileLauncherQuick(false);
    setDrawerOpen(false);
    navigate(to);
  };

  const openMobileLauncher = (
    preferredSpace = activeSpaceKey,
    { quick = false } = {}
  ) => {
    setDrawerOpen(false);
    setMobileAccountOpen(false);
    setMobileLauncherQuick(
      quick && preferredSpace === "mail"
    );

    const preferredGroup = mobileNavigationGroups.find(
      (group) => group.key === preferredSpace
    );

    setMobileLauncherSpace(
      preferredGroup?.items?.length > 1
        ? preferredGroup.key
        : null
    );
    setMobileMoreOpen(true);
  };

  const openMobileLauncherGroup = (group) => {
    if (!group) return;

    if (group.items.length === 1) {
      navigateMobile(group.items[0].to);
      return;
    }

    setMobileLauncherQuick(group.key === "mail");
    setMobileLauncherSpace(group.key);
  };

  // Destination du logo selon l'espace actuellement utilisé.
  const commerceHomePath =
    visibleCommerceNav[0]?.to || null;

  const gmaoHomePath =
    visibleGmaoNav.find(
      (item) => item.page === "dashboard"
    )?.to
    || visibleGmaoNav[0]?.to
    || null;

  const documentsHomePath =
    hasPageAccess(
      user,
      "documents",
      "documents"
    )
      ? "/app/documents"
      : null;

  const mailHomePath =
    canSeeMail
      ? "/app/mail"
      : null;

  const homePath =
    user.role === "saas_admin"
      ? "/monpremiermonitoring.fr/dashboard"
      : (
          canSeeGmao
          && [
            "maintenance_manager",
            "technician",
          ].includes(user.role)
        )
        ? gmaoHomePath
        : commerceHomePath
          || documentsHomePath
          || mailHomePath
          || gmaoHomePath
          || "/app/settings";

  const renderNavLink = (
    item,
    nested = false
  ) => {
    const Icon = item.icon || Wrench;

    const testId =
      item.to === "/app"
        ? "dashboard"
        : item.to.startsWith(
            "/app/commerce/"
          )
          ? `commerce-${item.to
              .split("/")
              .pop()}`
          : item.to.startsWith(
              "/app/gmao/"
            )
            ? `gmao-${item.to
                .split("/")
                .pop()}`
            : item.to
                .split("/")
                .pop();

    return (
      <li key={item.to}>
        <NavLink
          to={item.to}
          end={item.end}
          data-testid={`nav-${testId}`}
          className="nav-link group flex items-center gap-3 rounded-[10px] px-3 py-2 text-sm transition-all"
          style={({ isActive }) =>
            isActive
              ? {
                  background:
                    "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                  color: "#FFFFFF",
                  boxShadow:
                    "0 6px 18px -6px rgba(29, 78, 216, 0.55), 0 0 0 1px rgba(96, 165, 250, 0.35)",
                  fontWeight: 700,
                }
              : {
                  color:
                    "var(--do-ink-soft)",
                }
          }
        >
          {({ isActive }) => (
            <>
              <Icon
                size={nested ? 15 : 16}
                weight={
                  isActive
                    ? "fill"
                    : "regular"
                }
              />

              <span className="flex-1 font-heading font-semibold">
                {user.role ===
                  "technician" &&
                item.technicianLabel
                  ? item.technicianLabel
                  : item.label}
              </span>
            </>
          )}
        </NavLink>
      </li>
    );
  };


  const activeSettingsSection =
    new URLSearchParams(
      location.search
    ).get("section")
    || "profile";

  const requestedSupervisionSection =
    new URLSearchParams(
      location.search
    ).get("section");

  const activeSupervisionSection =
    SUPERVISION_NAV.some(
      (item) =>
        item.supervisionSection ===
        requestedSupervisionSection
    )
      ? requestedSupervisionSection
      : "global";

  const mailSearchParams =
    new URLSearchParams(
      location.search
    );

  const activeMailFolder =
    mailSearchParams.get("compose") === "1"
      ? "compose"
      : mailSearchParams.get("addressBook") === "1"
        ? "address-book"
        : mailSearchParams.get("createFolder") === "1"
          ? "create-folder"
          : mailSearchParams.get("folder") === "custom"
          ? (
            "custom:"
            + (
              mailSearchParams.get("id")
              || ""
            )
          )
          : (
            mailSearchParams.get("folder")
            || "inbox"
          );

  const renderDesktopNavLink = (item) => {
    const Icon = item.icon || House;

    const itemLabel =
      user.role === "technician"
      && item.technicianLabel
        ? item.technicianLabel
        : user.role === "maintenance_manager"
          && item.maintenanceLabel
            ? item.maintenanceLabel
            : item.label;

    const testId = item.to
      .replace(/^\/+/, "")
      .replace(/[^a-zA-Z0-9_-]/g, "-");

    return (
      <li key={item.to}>
        <NavLink
          to={item.to}
          end={item.end}
          data-testid={`desktop-nav-${testId}`}
          className={({ isActive }) => {
            const current =
              item.settingsSection
                ? location.pathname ===
                    "/app/settings"
                  && activeSettingsSection ===
                    item.settingsSection
                : item.supervisionSection
                  ? location.pathname ===
                      "/monpremiermonitoring.fr/dashboard"
                    && activeSupervisionSection ===
                      item.supervisionSection
                  : item.mailFolder
                    ? location.pathname ===
                        "/app/mail"
                      && activeMailFolder ===
                        item.mailFolder
                    : isActive;

            return `acta-desktop-context-link ${
              current ? "is-active" : ""
            }`;
          }}
        >
          <Icon
            size={18}
            weight="duotone"
          />
          <span>{itemLabel}</span>
          <CaretDown
            size={14}
            weight="bold"
            className="-rotate-90 opacity-45"
          />
        </NavLink>
      </li>
    );
  };


  const SidebarContent = (
    <>
      <div className="app-sidebar-brand h-16 flex items-center justify-between px-5 sm:px-6" style={{ borderBottom: "1px solid var(--do-border)" }}>
        <Link to={homePath} className="flex items-center gap-2.5 min-w-0">
          {branding.logoUrl ? (
            <img
              src={`${API}${branding.logoUrl}`}
              alt={branding.name || "Logo"}
              className="app-tenant-logo h-10 w-10 rounded-[11px] object-contain bg-[var(--do-bg-solid)] p-1.5 border border-[var(--do-border)] shadow-sm shrink-0"
              data-testid="tenant-logo"
            />
          ) : (
            <div
              className="app-default-logo h-9 w-9 rounded-[10px] flex items-center justify-center shrink-0"
              style={{
                background: "linear-gradient(135deg, var(--do-blue) 0%, var(--do-navy) 100%)",
                boxShadow: "0 6px 18px -6px rgba(29, 78, 216, 0.55), 0 0 0 1px rgba(96, 165, 250, 0.3)",
              }}
            >
              <span className="font-mono font-bold text-[13px]" style={{ color: "#FFFFFF" }}>A</span>
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-bold text-[15px] tracking-tight leading-tight truncate" style={{ color: "var(--do-ink)" }} data-testid="tenant-name">
              {branding.name || "AlmaCRM"}
            </div>
            {branding.logoUrl && (
              <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--do-muted)] leading-tight">
                via AlmaCRM
              </div>
            )}
          </div>
        </Link>
        {/* Close button (mobile only) */}
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden p-1.5 rounded-md transition-colors hover:bg-cobalt-500/10"
          style={{ color: "var(--do-muted)" }}
          data-testid="drawer-close"
          aria-label="Fermer le menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="px-6 mb-2 label-eyebrow">Navigation</div>
        <ul className="space-y-0.5 px-3">
          {visibleNav
            .filter(
              (item) =>
                item.to === "/app"
            )
            .map((item) =>
              renderNavLink(item)
            )}

          {canSeeCommerce && (
            <li
              className="py-0.5"
              data-testid="nav-commerce-group"
            >
              <button
                type="button"
                onClick={() =>
                  setCommerceOpen(
                    (current) => !current
                  )
                }
                aria-expanded={commerceOpen}
                aria-controls="commerce-navigation"
                data-testid="nav-commerce-toggle"
                className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left text-sm transition-all"
                style={{
                  color: commerceActive
                    ? "var(--do-accent)"
                    : "var(--do-ink-soft)",
                  background: commerceActive
                    ? "var(--do-surface-soft)"
                    : "transparent",
                  fontWeight: commerceActive
                    ? 700
                    : 600,
                }}
              >
                <Briefcase
                  size={17}
                  weight={
                    commerceActive
                      ? "fill"
                      : "regular"
                  }
                />

                <span className="flex-1 font-heading font-bold">
                  Commerce
                </span>

                <CaretDown
                  size={15}
                  weight="bold"
                  className={`transition-transform duration-200 ${
                    commerceOpen
                      ? "rotate-180"
                      : ""
                  }`}
                />
              </button>

              {commerceOpen && (
                <ul
                  id="commerce-navigation"
                  className="ml-4 mt-1 space-y-0.5 border-l border-[var(--do-border)] pl-2"
                  data-testid="nav-commerce-submenu"
                >
                  {visibleCommerceNav.map(
                    (item) =>
                      renderNavLink(
                        item,
                        true
                      )
                  )}
                </ul>
              )}
            </li>
          )}

          {canSeeGmao && (
            <li
              className="py-0.5"
              data-testid="nav-gmao-group"
            >
              <button
                type="button"
                onClick={() =>
                  setGmaoOpen(
                    (current) => !current
                  )
                }
                aria-expanded={gmaoOpen}
                aria-controls="gmao-navigation"
                data-testid="nav-gmao-toggle"
                className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left text-sm transition-all"
                style={{
                  color: gmaoActive
                    ? "var(--do-accent)"
                    : "var(--do-ink-soft)",
                  background: gmaoActive
                    ? "var(--do-surface-soft)"
                    : "transparent",
                  fontWeight: gmaoActive
                    ? 700
                    : 600,
                }}
              >
                <Wrench
                  size={17}
                  weight={
                    gmaoActive
                      ? "fill"
                      : "regular"
                  }
                />

                <span className="flex-1 font-heading font-bold">
                  GMAO
                </span>

                <CaretDown
                  size={15}
                  weight="bold"
                  className={`transition-transform duration-200 ${
                    gmaoOpen
                      ? "rotate-180"
                      : ""
                  }`}
                />
              </button>

              {gmaoOpen && (
                <ul
                  id="gmao-navigation"
                  className="ml-4 mt-1 space-y-0.5 border-l border-[var(--do-border)] pl-2"
                  data-testid="nav-gmao-submenu"
                >
                  {visibleGmaoNav.map(
                    (item) =>
                      renderNavLink(
                        item,
                        true
                      )
                  )}
                </ul>
              )}
            </li>
          )}

          {visibleNav
            .filter(
              (item) =>
                item.to !== "/app"
            )
            .map((item) =>
              renderNavLink(item)
            )}
        </ul>
      </nav>

      <div
        className="app-plan-card m-3 p-4 rounded-[14px] relative overflow-hidden shrink-0"
        style={{
          background: "linear-gradient(135deg, rgba(110, 231, 255, 0.10), rgba(37, 99, 235, 0.12))",
          border: "1px solid var(--do-border-strong)",
        }}
      >
        <div
          className="absolute -top-6 -right-6 h-20 w-20 rounded-full opacity-40 blur-2xl"
          style={{ background: "radial-gradient(circle, var(--do-cyan), transparent 70%)" }}
        />
        <div className="label-eyebrow" style={{ color: "var(--do-accent)" }}>Plan</div>
        <div className="font-display text-base uppercase mt-1" style={{ color: "var(--do-ink)" }}>
          {user.tenant?.plan || "—"}
        </div>
        <div className="text-xs mt-0.5 truncate" style={{ color: "var(--do-muted)" }}>{user.tenant?.name}</div>
      </div>
    </>
  );

  const DesktopSidebarContent = (
    <>
      <div
        className="acta-desktop-sidebar-brand"
        data-testid="desktop-sidebar-brand"
      >
        <Link
          to={homePath}
          className="flex min-w-0 items-center gap-3"
        >
          {branding.logoUrl ? (
            <img
              src={`${API}${branding.logoUrl}`}
              alt={branding.name || "Logo"}
              className="app-tenant-logo h-10 w-10 shrink-0 rounded-xl object-contain bg-[var(--do-bg-solid)] p-1.5"
              data-testid="desktop-tenant-logo"
            />
          ) : (
            <div className="acta-desktop-default-logo">
              <span>A</span>
            </div>
          )}

          <div className="min-w-0">
            <div
              className="truncate font-heading text-[15px] font-black"
              data-testid="desktop-tenant-name"
            >
              {branding.name || "Acta Documentum"}
            </div>
          </div>
        </Link>
      </div>

      <nav
        className="acta-context-sidebar-nav"
        data-testid="desktop-context-navigation"
        aria-label={desktopNavigationTitle}
      >
        <ul className="acta-context-sidebar-list">
          {activeDesktopNav.map(
            (item) =>
              renderDesktopNavLink(item)
          )}
        </ul>

        {documentsActive && (
          <div
            id="acta-documents-desktop-tree-slot"
            className="acta-documents-desktop-tree-slot"
            data-testid="documents-desktop-tree-slot"
          />
        )}

        {mailActive && (
          <div
            id="acta-mail-desktop-folders-slot"
            data-testid="mail-desktop-folders-slot"
          />
        )}

        {activeDesktopNav.length === 0
          && !documentsActive
          && !mailActive && (
          <div className="acta-context-sidebar-empty">
            Aucune page disponible dans cet espace.
          </div>
        )}
      </nav>

      <div
        className="acta-desktop-sidebar-account"
        data-testid="desktop-sidebar-account"
      >
        <div className="acta-desktop-sidebar-avatar">
          {(user.firstName?.[0] || "U")
            + (user.lastName?.[0] || "")}
        </div>

        <div className="min-w-0 flex-1">
          <strong data-support-private>
            {user.firstName} {user.lastName}
          </strong>
          <span>{desktopRoleLabel}</span>
        </div>

        <div className="acta-desktop-plan-pill">
          {user.tenant?.plan || "—"}
        </div>
      </div>
    </>
  );

  const mobileLauncherButton = !isSupervisionSpace && mobileNavigationGroups.length > 0 && (
        <button
          type="button"
          className={`acta-mobile-launcher-fab lg:hidden${mobileMoreOpen ? " is-open" : ""}${mobileLauncherSpace ? ` is-${mobileLauncherSpace}` : ""}`}
          data-launcher-space={mobileLauncherVisualSpace}
          style={mobileLauncherStyle}
          onClick={() => {
            if (mobileMoreOpen) {
              setMobileMoreOpen(false);
              return;
            }

            openMobileLauncher(
              activeSpaceKey,
              { quick: mailActive }
            );
          }}
          aria-label={mobileMoreOpen ? "Fermer la navigation" : "Ouvrir toutes les pages"}
          aria-expanded={mobileMoreOpen}
          aria-controls="acta-mobile-radial-navigation"
          data-testid="mobile-launcher-button"
        >
          {mobileMoreOpen
            ? <X size={25} weight="bold" />
            : <Planet size={27} weight="duotone" />}
        </button>
      );

  return (
    <div
      className="acta-app-shell min-h-0 overflow-hidden flex aurora-bg relative"
      data-acta-theme={appliedTheme}
      data-app-space={appSpace}
      data-active-workspace={activeSpaceKey}
      data-testid="app-layout"
    >
      {/* Barre latérale standard, dont le contenu s'adapte au module actif. */}
      <aside
        className="acta-desktop-sidebar app-sidebar-surface relative z-10 w-64 shrink-0 hidden lg:flex flex-col"
        style={{
          background: "var(--do-glass-bg)",
          backdropFilter: "blur(20px) saturate(140%)",
          borderRight: "1px solid var(--do-border)",
        }}
        data-testid="sidebar"
      >
        {DesktopSidebarContent}
      </aside>

      {/* Mobile navigation uses the bottom bar and dedicated sheets only. */}

      <div className="acta-shell-content relative h-full min-h-0 flex-1 flex flex-col min-w-0 overflow-hidden">
        <header
          className="relative z-[120] h-14 sm:h-16 shrink-0 flex items-center justify-between px-3 sm:px-5 lg:px-8 gap-2"
          style={{
            background: "var(--do-glass-bg)",
            backdropFilter: "blur(20px) saturate(140%)",
            borderBottom: "1px solid var(--do-border)",
          }}
          data-testid="header"
        >
          <div className="flex items-center gap-3 min-w-0">

            <div className="sm:hidden min-w-0">
              {mailActive ? (
                <button
                  type="button"
                  className="inline-flex min-h-10 items-center gap-2 rounded-[10px] px-2 py-1 text-left"
                  onClick={() => {
                    setMobileMoreOpen(false);
                    setMobileAccountOpen(false);
                    setDrawerOpen(true);
                  }}
                  aria-label="Ouvrir la navigation Messagerie"
                  aria-haspopup="dialog"
                  aria-expanded={drawerOpen}
                  data-testid="mobile-mail-navigation-trigger"
                >
                  <List
                    size={20}
                    weight="bold"
                    aria-hidden="true"
                    style={{ color: "var(--do-accent)" }}
                  />
                  <span
                    className="font-heading text-sm font-bold"
                    style={{ color: "var(--do-ink)" }}
                  >
                    Messagerie
                  </span>
                </button>
              ) : commerceActive ? (
                <Link
                  to="/app/commerce/dashboard"
                  className="acta-mobile-commercial-module-link"
                  aria-label="Accéder au tableau de bord commercial"
                  data-testid="mobile-commercial-module-link"
                >
                  <Briefcase
                    size={18}
                    weight="duotone"
                    aria-hidden="true"
                  />
                  <span>Module commercial</span>
                </Link>
              ) : (
                <>
                  <div
                    className="font-heading font-bold text-sm truncate"
                    style={{ color: "var(--do-ink)" }}
                    data-testid="mobile-page-title"
                  >
                    {currentMobileTitle}
                  </div>
                  <button
                    type="button"
                    className="acta-mobile-header-space"
                    onClick={() => openMobileLauncher(activeSpaceKey)}
                    aria-label={`Ouvrir la navigation. Espace actuel : ${activeSpaceLabel}`}
                    aria-haspopup="dialog"
                    aria-expanded={mobileMoreOpen}
                  >
                    <span>{activeSpaceLabel}</span>
                    <CaretDown size={12} weight="bold" />
                  </button>
                </>
              )}
            </div>
          </div>

          {desktopWorkspaceSpaces.length > 1 && (
            <nav
              className="acta-desktop-workspace-nav hidden lg:flex"
              aria-label="Espaces de travail"
              data-testid="desktop-workspace-navigation"
            >
              {desktopWorkspaceSpaces.map((space) => {
                const Icon = space.icon;

                return (
                  <button
                    key={space.key}
                    type="button"
                    data-workspace={space.key}
                    className={`acta-desktop-workspace-tab ${
                      space.active ? "is-active" : ""
                    }`}
                    onClick={() =>
                      navigateMobile(space.to)
                    }
                    aria-current={
                      space.active
                        ? "page"
                        : undefined
                    }
                    title={space.label}
                  >
                    <Icon
                      size={18}
                      weight={
                        space.active
                          ? "fill"
                          : "duotone"
                      }
                    />
                    <span>{space.label}</span>
                  </button>
                );
              })}
            </nav>
          )}

          <div className="acta-header-actions flex items-center gap-2 sm:gap-3 lg:gap-4 shrink-0">
            <div
              className="acta-theme-switch hidden lg:inline-flex"
                role="group"
                aria-label="Choisir le thème d’affichage"
                data-testid="desktop-theme-switch"
              >
                <button
                  type="button"
                  className={appliedTheme === "light" ? "is-active" : ""}
                  onClick={() => handleThemeChange("light")}
                  aria-pressed={appliedTheme === "light"}
                  disabled={themeSaving}
                  data-testid="theme-day-button"
                  title="Activer le thème jour"
                  aria-label="Activer le thème jour"
                >
                  <Sun
                    size={17}
                    weight={appliedTheme === "light" ? "fill" : "bold"}
                  />
                </button>

                <button
                  type="button"
                  className={appliedTheme === "dark" ? "is-active" : ""}
                  onClick={() => handleThemeChange("dark")}
                  aria-pressed={appliedTheme === "dark"}
                  disabled={themeSaving}
                  data-testid="theme-night-button"
                  title="Activer le thème nuit"
                  aria-label="Activer le thème nuit"
                >
                  <Moon
                    size={17}
                    weight={appliedTheme === "dark" ? "fill" : "bold"}
                  />
                </button>
              </div>

            {canUseActaAgent && (
              <button
                type="button"
                onClick={() => {
                  setMobileMoreOpen(false);
                  setMobileAccountOpen(false);
                  setActaAgentOpen(true);
                }}
                className="flex h-9 min-w-9 items-center justify-center rounded-xl border border-[var(--do-border-strong)] bg-[var(--do-accent-soft)] px-2 text-xs font-black text-[var(--do-accent)]"
                data-testid="acta-agent-button"
                aria-label="Ouvrir l’Agent Acta"
                title="Agent Acta"
              >
                IA
              </button>
            )}

            <NotificationsDropdown />
            <div className="hidden sm:block h-6 w-px" style={{ background: "var(--do-border-strong)" }} />

            {/* Desktop: full user block */}
            <div className="acta-header-account hidden md:flex items-center gap-3">
              <div className="acta-header-account-identity text-right">
                <div
                  className="text-sm font-semibold leading-tight"
                  data-testid="user-name"
                  data-support-private
                  style={{ color: "var(--do-ink)" }}
                >
                  {user.firstName} {user.lastName}
                </div>
                <div
                  className="text-[11px] leading-tight truncate max-w-[180px]"
                  data-support-private
                  style={{ color: "var(--do-muted)" }}
                >
                  {user.email}
                </div>
              </div>
              <div
                className="h-9 w-9 rounded-[12px] flex items-center justify-center font-display text-sm shrink-0"
                data-support-private
                style={{
                  background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                  color: "#FFFFFF",
                  boxShadow: "0 6px 18px -6px rgba(29, 78, 216, 0.55)",
                }}
              >
                {(user.firstName?.[0] || "U") + (user.lastName?.[0] || "")}
              </div>
              <button
                onClick={onLogout}
                className="transition-colors hover:text-[var(--do-accent)]"
                style={{ color: "var(--do-muted)" }}
                data-testid="logout-btn"
                title="Se déconnecter"
              >
                <SignOut size={18} />
              </button>
            </div>

            <button
              type="button"
              className="acta-mobile-profile-button flex md:hidden"
              onClick={() => {
                setMobileMoreOpen(false);
                setMobileAccountOpen(true);
              }}
              data-testid="mobile-profile-menu"
              aria-label="Ouvrir le menu du compte"
            >
              <span
                className="h-8 w-8 rounded-[10px] flex items-center justify-center font-display text-xs"
                data-support-private
                style={{
                  background: "linear-gradient(135deg, var(--do-blue), var(--do-navy))",
                  color: "#FFFFFF",
                }}
              >
                {(user.firstName?.[0] || "U") + (user.lastName?.[0] || "")}
              </span>
            </button>
          </div>
        </header>

        <main className="acta-mobile-main relative min-h-0 flex-1 overflow-y-auto">
          {children || <Outlet />}
        </main>


      </div>

      {mailActive && (
        <Sheet
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
        >
          <SheetContent
            side="left"
            showClose={false}
            overlayClassName="z-[100] top-[calc(58px+env(safe-area-inset-top,0px))] bg-[var(--do-overlay)]/70 backdrop-blur-[2px] lg:hidden"
            className="z-[110] inset-y-auto left-3 top-[calc(58px+env(safe-area-inset-top,0px)+6px)] h-auto max-h-[calc(100dvh-76px-env(safe-area-inset-top,0px)-env(safe-area-inset-bottom,0px))] w-max max-w-[calc(100vw-24px)] overflow-hidden rounded-[18px] border border-[var(--do-border)] bg-[color-mix(in_srgb,var(--do-glass-bg)_78%,transparent)] p-0 text-[var(--do-ink)] shadow-2xl backdrop-blur-2xl lg:hidden"
            data-testid="mobile-mail-navigation-sheet"
          >
            <SheetTitle className="sr-only">
              Navigation Messagerie
            </SheetTitle>

            <nav
              className="flex max-h-[inherit] min-h-0 flex-col overflow-y-auto px-3 py-3"
              aria-label="Navigation Messagerie"
            >
              <ul className="space-y-1">
                {MAIL_NAV.map((item) => {
                  const Icon = item.icon || EnvelopeOpen;
                  const isActive =
                    activeMailFolder === item.mailFolder;

                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        className={[
                          "flex min-h-11 w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left text-sm font-semibold transition-colors",
                          isActive
                            ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                            : "text-[var(--do-ink-soft)] hover:bg-[var(--do-surface-soft)]",
                        ].join(" ")}
                        onClick={() => navigateMobile(item.to)}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <Icon
                          size={19}
                          weight={isActive ? "fill" : "regular"}
                          aria-hidden="true"
                        />
                        <span className="whitespace-nowrap">
                          {item.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="my-3 border-t border-[var(--do-border)]" />

              <div className="mb-2 flex items-center justify-between px-3">
                <span className="label-eyebrow">
                  Dossiers
                </span>
              </div>

              <ul className="space-y-1 pb-6">
                {mailCustomNavigation.map((item) => {
                  const Icon = item.icon || Folder;
                  const isActive =
                    activeMailFolder === item.mailFolder;

                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        className={[
                          "flex min-h-11 w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left text-sm font-semibold transition-colors",
                          isActive
                            ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                            : "text-[var(--do-ink-soft)] hover:bg-[var(--do-surface-soft)]",
                        ].join(" ")}
                        onClick={() => navigateMobile(item.to)}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <Icon
                          size={19}
                          weight={isActive ? "fill" : "regular"}
                          aria-hidden="true"
                        />
                        <span className="whitespace-nowrap">
                          {item.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </SheetContent>
        </Sheet>
      )}

      {mobileLauncherButton}

      {mobileMoreOpen && mobileNavigationGroups.length > 0 && (
        <div
          id="acta-mobile-radial-navigation"
          className="acta-mobile-launcher-layer lg:hidden"
          data-launcher-space={mobileLauncherSpace || "root"}
          data-launcher-version="v18"
          style={mobileLauncherStyle}
          data-testid="mobile-radial-navigation"
        >
          <button
            type="button"
            className="acta-mobile-launcher-backdrop"
            onClick={() => setMobileMoreOpen(false)}
            aria-label="Fermer la navigation"
          />

          <section
            className="acta-mobile-launcher-panel"
            data-launcher-space={mobileLauncherSpace || "root"}
            data-launcher-version="v18"
            data-launcher-count={mobileLauncherSpace ? mobileLauncherItems.length : mobileLauncherRootItems.length}
            style={mobileLauncherStyle}
            role="dialog"
            aria-modal="true"
            data-acta-modal-kind="navigation"
            aria-label="Toutes les pages"
          >
            {selectedMobileNavigationGroup ? (
              <>
                <div className="acta-mobile-launcher-heading">
                  <MobileLauncherHeadingIcon
                    size={20}
                    weight="duotone"
                    aria-hidden="true"
                  />
                  <div>
                    <strong>{selectedMobileNavigationGroup.label}</strong>
                    <span>{mobileLauncherItems.length} page{mobileLauncherItems.length > 1 ? "s" : ""}</span>
                  </div>
                </div>

                <div
                  className="acta-mobile-launcher-orbit"
                  aria-label={`Pages ${selectedMobileNavigationGroup.label}`}
                >
                  <button
                    type="button"
                    className="acta-mobile-launcher-center-back"
                    onClick={() => setMobileLauncherSpace(null)}
                    aria-label="Revenir aux espaces"
                  >
                    <CaretDown
                      size={20}
                      weight="bold"
                      className="rotate-90"
                      aria-hidden="true"
                    />
                    <span>Retour</span>
                  </button>

                  {mobileLauncherItems.map((item, index) => {
                    const Icon = item.icon || House;
                    const isActive = mobileNavigationItemIsActive(item);
                    const position = mobileLauncherPosition(
                      selectedMobileNavigationGroup.key,
                      item,
                      index,
                      mobileLauncherItems.length
                    );

                    return (
                      <button
                        key={item.to}
                        type="button"
                        className={`acta-mobile-launcher-item${isActive ? " is-active" : ""}`}
                        style={{
                          "--launcher-x": `${position.x}%`,
                          "--launcher-y": `${position.y}%`,
                          "--acta-launcher-index": index,
                        }}
                        onClick={() => navigateMobile(item.to)}
                        aria-current={isActive ? "page" : undefined}
                        aria-label={item.label}
                      >
                        <Icon size={20} weight={isActive ? "fill" : "regular"} />
                        <span>{mobileNavigationLabel(item)}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className="acta-mobile-launcher-heading is-root">
                  <Planet
                    size={20}
                    weight="duotone"
                    aria-hidden="true"
                  />
                  <div>
                    <strong>Navigation</strong>
                    <span>{mobileLauncherRootItems.length} espaces</span>
                  </div>
                </div>

                <div
                  className="acta-mobile-launcher-orbit is-root"
                  aria-label="Espaces de travail"
                >
                  {mobileLauncherRootItems.map((group, index) => {
                    const Icon = group.icon || House;
                    const isActive = group.key === activeSpaceKey;
                    const position = mobileLauncherPosition(
                      "root",
                      group,
                      index,
                      mobileLauncherRootItems.length
                    );

                    return (
                      <button
                        key={group.key}
                        type="button"
                        data-launcher-module={group.key}
                        className={`acta-mobile-launcher-item is-space${isActive ? " is-active" : ""}`}
                        style={{
                          "--launcher-x": `${position.x}%`,
                          "--launcher-y": `${position.y}%`,
                          "--acta-launcher-index": index,
                        }}
                        onClick={() => openMobileLauncherGroup(group)}
                        aria-label={group.label}
                      >
                        <Icon size={22} weight={isActive ? "fill" : "regular"} />
                        <span>{group.shortLabel || group.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {mobileAccountOpen && (
        <div
          className="acta-mobile-more-layer lg:hidden"
          data-testid="mobile-account-sheet"
        >
          <button
            type="button"
            className="acta-mobile-more-backdrop"
            onClick={() => setMobileAccountOpen(false)}
            aria-label="Fermer le menu du compte"
          />

          <section
            className="acta-mobile-more-sheet acta-mobile-account-sheet"
            role="dialog"
            aria-modal="true"
            data-acta-modal-kind="sheet"
            aria-label="Compte"
          >
            <div className="acta-mobile-sheet-handle" aria-hidden="true" />
            <div className="acta-mobile-more-header">
              <div>
                <p>Compte</p>
                <h2>{user.firstName} {user.lastName}</h2>
              </div>
              <button
                type="button"
                onClick={() => setMobileAccountOpen(false)}
                aria-label="Fermer"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            <div className="acta-mobile-more-scroll">
              <div className="acta-mobile-account-card">
                <UserCircle size={30} weight="duotone" />
                <div>
                  <strong>{user.firstName} {user.lastName}</strong>
                  <span>{roleLabel(user.role)}</span>
                </div>
              </div>
              <div className="acta-mobile-more-list">
                <button
                  type="button"
                  onClick={() => navigateMobile("/app/settings")}
                >
                  <Gear size={20} />
                  <span>Paramètres du compte</span>
                  <CaretDown size={15} className="-rotate-90" />
                </button>
                <button
                  type="button"
                  className="is-danger"
                  onClick={onLogout}
                  data-testid="logout-btn-mobile"
                >
                  <SignOut size={20} />
                  <span>Se déconnecter</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      )}

      <ActaAgent
        open={actaAgentOpen}
        onClose={() =>
          setActaAgentOpen(false)
        }
        user={user}
        hasFeature={hasFeature}
        pathname={location.pathname}
        navigate={navigate}
        onPreparedShare={({
          prospect,
          document,
        }) => {
          setActaAgentOpen(false);

          setAgentPreparedShare({
            prospect,
            document,
          });
        }}
      />

      {agentPreparedShare && (
        <ShareModal
          item={{
            ...agentPreparedShare.document,
            type: "document",
          }}
          defaultProspect={
            agentPreparedShare.prospect
          }
          onClose={() =>
            setAgentPreparedShare(null)
          }
        />
      )}

    </div>
  );
}
